import { urlQuery, slugifyPath } from 'lib/helpers'
import { slugify } from 'lib/slugify'
import { updateAppUrl } from 'lib/url/route'
import {
  FacetsAndQueryParamsObject,
  GenerateLocationUrlParams,
  GetFirstFacetValueParams,
  GetHrefFunctionParams,
  HrefReturnFunctionParams,
  RemoveUsedFacetsParams,
  ValidSharedFacetParams,
} from '../types'
import { browseBooleanFacets } from 'lib/browse'

/*
  href: full path of the new page you want to navigate. Ex: /account/artworks/1979882?size=small
  VERY IMPORTANT TO NOT RELOAD ALL THE PAGE
  nextAppRoute: this is the nextJS app route logic, you need to check where is the page that is calling this function and get it's location from the "apps" folder.
  For instance, for the href example in this comment, it is the page artist all artworks that could have a path route like `/account/artworks/<userId>`, but the nextAppRoute is: `/user/artworks`
*/
export const updateClientSideUrl = (href: string, nextAppRoute: string): void => {
  updateAppUrl({
    app: nextAppRoute,
    href,
  })
}

export const isValidSharedFacet = ({
  sharedFacetNames,
  facetValue,
  facetName,
}: ValidSharedFacetParams): boolean => {
  // We need to SLUGIFY the Value
  const value = slugify(facetValue)
  // Skip items that SHARE Facet Values Ex: abstract: ['styles', 'subject'],
  // We will abstract for `styles`, but `subject` we will skip
  // EXIT, return if FIRST ITEM is !== FacetName
  return !(sharedFacetNames && sharedFacetNames[value] && sharedFacetNames[value][0] !== facetName)
}

export const getFirstFacetValue = ({
  facetName,
  facetValues,
  sharedFacetNames,
  booleanFacetsToString = browseBooleanFacets,
}: GetFirstFacetValueParams): string => {
  const [facetValue] = facetValues || [] // Get First Item/Value

  if (facetName === 'has_prints') {
    return facetValue ? 'prints' : ''
  }

  if (facetName === 'category') {
    return facetValue || 'All'
  }

  if (Object.keys(booleanFacetsToString).includes(facetName)) {
    return facetValue ? booleanFacetsToString[facetName] : ''
  }

  if (
    !isValidSharedFacet({
      sharedFacetNames,
      facetValue,
      facetName,
    })
  ) {
    return ''
  }

  return facetValue || ''
}

/*
Helper to remove values from Object
Used specifically for URL creation
Params ({
  facets: {category: [], styles: ['Abstract', 'Fine Art'], medium: ...},
  usedValues: { styles: 'Abstract'}
})
Will only return Values ( Non Emptry Arrays )
Result: {styles: ['Fine Art']}
pathParamBooleanFacetNameList: ['is_outdoor'] is an array of facetName to always remove from queryParams as this is going to be only a pathParam
*/
export const removeUsedFacets = ({
  facetsAndQueryParams,
  usedValues,
  pathParamBooleanFacetNameList,
}: RemoveUsedFacetsParams): FacetsAndQueryParamsObject => {
  const facetResults = { ...facetsAndQueryParams }
  Object.entries(usedValues).forEach(([facetName, attributeValue]) => {
    if (
      attributeValue &&
      facetsAndQueryParams[facetName] &&
      facetsAndQueryParams[facetName].length
    ) {
      if (Array.isArray(facetsAndQueryParams[facetName])) {
        facetResults[facetName] = facetsAndQueryParams[facetName].filter(
          (i) => i !== attributeValue
        )
      } else if (facetResults[facetName] === attributeValue) {
        facetResults[facetName] = ''
      }
    }
    if (pathParamBooleanFacetNameList.includes(facetName)) {
      facetResults[facetName] = []
    }
  })
  return facetResults
}

export const generateLocationUrl = ({
  facetsAndQueryParams,
  pathParams,
  sharedFacetNames,
  booleanFacetsToString = browseBooleanFacets,
  isPrintsPage,
  notSlugifiedFacetNames = [],
}: GenerateLocationUrlParams): string => {
  const urlPathParams: Record<string, string> = pathParams.reduce(
    (acc, facetName) => ({
      ...acc,
      [facetName]: getFirstFacetValue({
        facetName,
        facetValues: facetsAndQueryParams[facetName],
        sharedFacetNames,
        booleanFacetsToString,
      }),
    }),
    {}
  )

  if (isPrintsPage) {
    urlPathParams['has_prints'] = 'prints'
  }

  // Product Note -> restore URL rules for /prints instead of /prints/all
  if (urlPathParams?.has_prints && urlPathParams?.category === 'All') {
    delete urlPathParams.category
  }

  const pathString = slugifyPath(
    Object.values(urlPathParams)
      .filter(Boolean)
      .map(String)
  )
  const queryCleanup = removeUsedFacets({
    facetsAndQueryParams,
    usedValues: { ...urlPathParams, has_prints: true }, // Never append `has_prints` to `?` query
    pathParamBooleanFacetNameList: Object.keys(booleanFacetsToString) || [],
  })
  const queryString = urlQuery(queryCleanup, ['query', ...notSlugifiedFacetNames]) // always keep 'query' query param not slugified

  return `${pathString}${queryString}`
}

export const getHrefFunction = ({
  facetsAndQueryParams,
  pathParams,
  sharedFacetNames,
  notSlugifiedFacetNames,
}: GetHrefFunctionParams): ((params?: HrefReturnFunctionParams) => string) => {
  // We want a way to create pass in PARAMS and create UNIQUE url
  return (params?: HrefReturnFunctionParams): string => {
    const path = typeof window !== 'undefined' ? window?.location?.pathname : ''
    const list = (path || '').split('/').filter(Boolean)
    const isPrintsPage = list[0] === 'prints'

    return generateLocationUrl({
      facetsAndQueryParams: { ...facetsAndQueryParams, ...(params || {}) },
      // facetsAndQueryParams: { ...facetsAndQueryParams, ...(params || {}) },
      // Params are OVERRIDES to create URL
      pathParams,
      sharedFacetNames,
      isPrintsPage,
      notSlugifiedFacetNames,
    })
  }
}
